import React, { useCallback, useMemo, useReducer, useContext } from 'react';
import PropTypes from 'prop-types';
import { useCookies } from 'react-cookie';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { useMediaQuery } from 'react-responsive';

import ModalWrapper from 'components/rentingRedRac/Modal/Wrapper';
import Traductor from 'components/Traductor';
import Button from 'components/bronson/Button';
import formPages from 'components/rentingRedRac/Purchase/formPages';

import RentingService from 'services/RentingService';
import Globals from '../../../Globals';
import { UserContext } from 'contexts/UserContext';
import { vehicleTypes } from 'components/rentingRedRac/types';
import { validateDocumentId } from 'Functions';

const initialForm = {
    km: '',
    amount: '',
    documentId: '',
    paymentMethod: 'C',
    name: '',
    firstSurname: '',
    secondSurname: '',
    email: '',
    secondaryEmail: '',
    phone: '',
    secondaryPhone: '',
    business: '',
    streetType: '',
    street: '',
    streetNumber: '',
    baseAmount: '',
    financialAmount: '',
    streetFloor: '',
    postalCode: '',
    city: '',
    poblation: '',
    fiscalPoblation: '',
    fiscalStreetType: '',
    fiscalStreet: '',
    fiscalStreetNumber: '',
    fiscalStreetFloor: '',
    fiscalPostalCode: '',
    fiscalCity: '',
    copyDirection: false,
    autocompleted: false,
    idCustomer: null,
};

const initialFormState = {
    buyer: null,
    loading: false,
    page: 0,
    totalPages: formPages.length - 1,
};

const formReducer = (state, { type, payload }) => {
    switch (type) {
        case 'UPDATE_FORM_PAGE':
            document.querySelector(
                '.c-modal--detail-vehicle .c-modal__content.u-pt-none'
            ).scrollTop = 0;
            return { ...state, page: state.page + payload, loading: false };
        case 'UPDATE_STATUS':
            return { ...state, loading: payload };
        default:
            return state;
    }
};

const Form = ({ onShowPurchase, vehicle, removeVehicle }) => {
    const t = useMemo(() => new Traductor(), []);
    const {
        user: { data: user },
    } = useContext(UserContext);
    const [
        {
            [Globals.COOKIES.JWT]: { AccessToken },
        },
    ] = useCookies([Globals.COOKIES.JWT]);
    const yupFiscalValidation = useCallback(
        () =>
            Yup.string().when('autocompleted', {
                is: false,
                then: () =>
                    Yup.string().when('copyDirection', {
                        is: false,
                        then: (schema) => schema.required(
                            t.getTraduction('RequiredFieldError')
                        ),
                    }),
            }),
        [t]
    );
    const yupRequiredString = Yup.string().required(
        t.getTraduction('RequiredFieldError')
    );
    const isMobile = useMediaQuery({
        maxDeviceWidth: 600,
    });

    const [{ loading, page, totalPages }, dispatch] = useReducer(
        formReducer,
        initialFormState
    );

    const fetchCustomer = useCallback(
        async (documentId, setFieldValue) => {
            try {
                dispatch({ type: 'UPDATE_STATUS', payload: true });
                const response = await RentingService.getCustomer(
                    { documentId },
                    AccessToken
                );

                if (response?.body?.Result) {
                    setFieldValue('autocompleted', true);
                    const {
                        Email1,
                        Email2,
                        Name,
                        FirstSurname,
                        SecondSurname,
                        Phone1,
                        Phone2,
                        Fiscal,
                        Physical,
                        IdCustomer,
                    } = response?.body?.Result;

                    setFieldValue('idCustomer', IdCustomer);
                    setFieldValue('email', Email1 || '');
                    setFieldValue('phone', Phone1 || '');
                    setFieldValue('secondaryEmail', Email2 || '');
                    setFieldValue('secondaryPhone', Phone2 || '');

                    const documentType = validateDocumentId(documentId);
                    if (documentType === 1 || documentType === 3) {
                        setFieldValue('name', Name || '');
                        setFieldValue('firstSurname', FirstSurname || '');
                        setFieldValue('secondSurname', SecondSurname || '');
                    } else {
                        setFieldValue('business', FirstSurname || ''); //if company fill business else name and surnames
                    }

                    setFieldValue('fiscalPoblation', Fiscal?.Town || '');
                    setFieldValue(
                        'fiscalStreetType',
                        Fiscal?.IdStreetType || ''
                    );
                    setFieldValue('fiscalStreet', Fiscal?.StreetName || '');
                    setFieldValue(
                        'fiscalStreetFloor',
                        Fiscal?.StreetFloor || ''
                    );
                    setFieldValue(
                        'fiscalStreetNumber',
                        Fiscal?.StreetNumber || ''
                    );
                    setFieldValue('fiscalPostalCode', Fiscal?.PostalCode || '');
                    setFieldValue('fiscalCity', Fiscal?.Province || '');

                    setFieldValue('poblation', Physical?.Town || '');
                    setFieldValue('streetType', Physical?.IdStreetType || '');
                    setFieldValue('street', Physical?.StreetName || '');
                    setFieldValue('streetFloor', Physical?.StreetFloor || '');
                    setFieldValue('streetNumber', Physical?.StreetNumber || '');
                    setFieldValue('postalCode', Physical?.PostalCode || '');
                    setFieldValue('city', Physical?.Province || '');
                } else {
                    setFieldValue('autocompleted', false);
                    setFieldValue('idCustomer', null);
                }
                dispatch({
                    type: 'UPDATE_FORM_PAGE',
                    payload: 1,
                });
            } catch (error) {
                console.log(error);
            }
        },
        [AccessToken]
    );

    const onSubmit = useCallback(
        async ({ autocompleted, copyDirection, ...form }, { resetForm }) => {
            try {
                dispatch({ type: 'UPDATE_STATUS', payload: true });
                let body = {
                    ...form,
                    policyId: vehicle?.Id,
                    idEmployee: user.Empleado.Id,
                    idProfile: user.Profile,
                };
                if (autocompleted) {
                    body = {
                        km: form.km,
                        paymentMethod: form.paymentMethod,
                        policyId: vehicle?.Id,
                        idCustomer: form.idCustomer,
                    };
                } else if (copyDirection) {
                    body = Object.entries(form).reduce(
                        (acc, [key, value]) => {
                            if (!key.includes('fiscal')) acc[key] = value;
                            return acc;
                        },
                        {
                            copyDirection,
                            policyId: vehicle?.Id,
                        }
                    );
                }
                body.amount = form.amount.replace(',', '.');
                if (form.paymentMethod === 'F') {
                    body.baseAmount = form.baseAmount.replace(',', '.');
                    body.financialAmount = form.financialAmount.replace(
                        ',',
                        '.'
                    );
                }

                const res = await RentingService.buyVehicle(body, AccessToken);
                if (res?.body?.Result) {
                    removeVehicle(vehicle?.Id);
                    return onShowPurchase({ removeSelectedVehicle: true });
                } else {
                    alert(t.getTraduction('RequestErrorMessage'));
                }
            } catch (error) {
                console.log(error);
            } finally {
                dispatch({ type: 'UPDATE_STATUS', payload: false });
            }
        },
        [
            AccessToken,
            onShowPurchase,
            removeVehicle,
            t,
            user.Empleado.Id,
            user.Profile,
            vehicle?.Id,
        ]
    );

    const renderFormPage = useCallback(
        (values, errors, touched, setFieldValue) => {
            const PageComponent = formPages[page];
            const renderError = name =>
                touched[name] &&
                errors[name] && (
                    <p className="c-error-message">*{errors[name]}</p>
                );

            return (
                <PageComponent
                    t={t}
                    vehicle={vehicle}
                    values={values}
                    renderError={renderError}
                    setFieldValue={setFieldValue}
                />
            );
        },
        [page, t, vehicle]
    );

    return (
        <>
            <ModalWrapper onShowModal={onShowPurchase}>
                <section className="o-layout u-m-none">
                    <article className="o-layout__item u-m-none u-mb u-pl-none">
                        <header className="o-layout__item u-p-none u-m-none u-mb-large">
                            <h3 className="u-m-none">
                                {t.getTraduction('Buy')}
                            </h3>
                        </header>
                    </article>
                    <Formik
                        initialValues={initialForm}
                        onSubmit={onSubmit}
                        validationSchema={Yup.object().shape({
                            km: yupRequiredString,
                            amount: yupRequiredString.test(
                                'amount',
                                t.getTraduction('ErrorMinimumImport'),
                                val =>
                                    vehicle?.MinimumPrice <=
                                    parseFloat(val?.replace(',', '.'))
                            ),
                            documentId: yupRequiredString
                                .test(
                                    'documentId format',
                                    t.getTraduction('InvalidFormat'),
                                    val => validateDocumentId(val)
                                )
                                .max(9, t.getTraduction('InvalidFormat')),
                            paymentMethod: Yup.string()
                                .oneOf(['C', 'F'])
                                .required(),
                            baseAmount: Yup.string().when('paymentMethod', {
                                is: 'F',
                                then: (schema) => schema
                                    .required(
                                        t.getTraduction('RequiredFieldError')
                                    )
                                    .test(
                                        'Base and financial amounts equal total amount',
                                        t.getTraduction('ErrorFinancialAmount'),
                                        function (val) {
                                            const leftAmount =
                                                Math.round(
                                                    (this.resolve(
                                                        Yup.ref('amount')
                                                    )?.replace(',', '.') -
                                                        this.resolve(
                                                            Yup.ref(
                                                                'financialAmount'
                                                            )
                                                        )?.replace(',', '.') +
                                                        Number.EPSILON) *
                                                        100
                                                ) / 100;
                                            return (
                                                +val?.replace(',', '.') ===
                                                leftAmount
                                            );
                                        }
                                    ),
                            }),
                            financialAmount: Yup.string().when(
                                'paymentMethod',
                                {
                                    is: 'F',
                                    then: (schema) => schema
                                        .required(
                                            t.getTraduction(
                                                'RequiredFieldError'
                                            )
                                        )
                                        .test(
                                            'Base and financial amounts equal total amount',
                                            t.getTraduction(
                                                'ErrorFinancialAmount'
                                            ),
                                            function (val) {
                                                const leftAmount =
                                                    Math.round(
                                                        (this.resolve(
                                                            Yup.ref('amount')
                                                        )?.replace(',', '.') -
                                                            this.resolve(
                                                                Yup.ref(
                                                                    'baseAmount'
                                                                )
                                                            )?.replace(
                                                                ',',
                                                                '.'
                                                            ) +
                                                            Number.EPSILON) *
                                                            100
                                                    ) / 100;
                                                return (
                                                    +val?.replace(',', '.') ===
                                                    leftAmount
                                                );
                                            }
                                        ),
                                }
                            ),
                            copyDirection: Yup.bool(),
                            ...(page === totalPages
                                ? {
                                      email: yupRequiredString.email(
                                          t.getTraduction('InvalidFormat')
                                      ),
                                      secondaryEmail: Yup.string().email(
                                          t.getTraduction('InvalidFormat')
                                      ),
                                      phone: yupRequiredString.max(
                                          14,
                                          t.getTraduction('LengthError')(14)
                                      ),
                                      secondaryPhone: Yup.string().max(
                                          14,
                                          t.getTraduction('LengthError')(14)
                                      ),
                                      business: Yup.string().when(
                                          'documentId',
                                          {
                                              is: value =>
                                                  validateDocumentId(value) ===
                                                  2,
                                              then: (schema) => schema.required(
                                                  t.getTraduction(
                                                      'RequiredFieldError'
                                                  )
                                              ),
                                          }
                                      ),
                                      name: Yup.string().when('documentId', {
                                          is: value =>
                                              validateDocumentId(value) === 1 ||
                                              validateDocumentId(value) === 3,
                                          then: (schema) => schema.required(
                                              t.getTraduction(
                                                  'RequiredFieldError'
                                              )
                                          ),
                                      }),
                                      firstSurname: Yup.string().when(
                                          'documentId',
                                          {
                                              is: value =>
                                                  validateDocumentId(value) ===
                                                      1 ||
                                                  validateDocumentId(value) ===
                                                      3,
                                              then: (schema) => schema.required(
                                                  t.getTraduction(
                                                      'RequiredFieldError'
                                                  )
                                              ),
                                          }
                                      ),
                                      secondSurname: Yup.string(),
                                      streetType: yupRequiredString,
                                      street: yupRequiredString.max(
                                          99,
                                          t.getTraduction('LengthError')(99)
                                      ),
                                      streetNumber: yupRequiredString,
                                      streetFloor: Yup.string(),
                                      postalCode: yupRequiredString,
                                      city: yupRequiredString,
                                      poblation: yupRequiredString,
                                      fiscalStreetType: yupFiscalValidation(),
                                      fiscalStreet: yupFiscalValidation().max(
                                          99,
                                          t.getTraduction('LengthError')(99)
                                      ),
                                      fiscalStreetNumber: yupFiscalValidation(),
                                      fiscalPostalCode: yupFiscalValidation(),
                                      fiscalCity: yupFiscalValidation(),
                                      fiscalPoblation: yupFiscalValidation(),
                                  }
                                : {}),
                        })}
                    >
                        {({
                            values,
                            handleSubmit,
                            setFieldValue,
                            setTouched,
                            errors,
                            touched,
                            validateForm,
                        }) => (
                            <form onSubmit={handleSubmit}>
                                <div className="o-layout__item u-m-none u-mb u-pl-none">
                                    <article>
                                        {renderFormPage(
                                            values,
                                            errors,
                                            touched,
                                            setFieldValue
                                        )}
                                        <footer
                                            className="u-flex u-mt-xlarge"
                                            style={{
                                                justifyContent: 'center',
                                                flexDirection: isMobile
                                                    ? 'column-reverse'
                                                    : 'row',
                                            }}
                                        >
                                            <Button
                                                options={{
                                                    id: 'cancel-purchase-button',
                                                    text: t.getTraduction(
                                                        page ? 'Back' : 'Cancelar'
                                                    ),
                                                    class: `c-btn c-btn--secondary u-1/6 u-1/3@s u-flex ${
                                                        isMobile
                                                            ? ''
                                                            : 'u-inline-item u-mr'
                                                    }`,
                                                    type: 'button',
                                                }}
                                                onClick={
                                                    page
                                                        ? () =>
                                                              dispatch({
                                                                  type:
                                                                      'UPDATE_FORM_PAGE',
                                                                  payload: -1,
                                                              })
                                                        : onShowPurchase
                                                }
                                            />
                                            <Button
                                                options={{
                                                    id: 'submit-purchase-button',
                                                    text: t.getTraduction(
                                                        page ? 'Buy' : 'Next'
                                                    ),
                                                    type: `${
                                                        page === totalPages
                                                            ? 'submit'
                                                            : 'button'
                                                    }`,
                                                    class: `c-btn u-1/6 u-1/3@s u-flex ${
                                                        isMobile
                                                            ? 'u-mb'
                                                            : 'u-inline-item'
                                                    }`,
                                                    disabled: loading,
                                                    loading,
                                                }}
                                                onClick={
                                                    page === totalPages
                                                        ? undefined
                                                        : async () => {
                                                              const errors = await validateForm();
                                                              if (
                                                                  !Object.keys(
                                                                      errors
                                                                  ).length
                                                              ) {
                                                                  fetchCustomer(
                                                                      values.documentId,
                                                                      setFieldValue
                                                                  );
                                                              } else {
                                                                  setTouched(
                                                                      errors
                                                                  );
                                                              }
                                                          }
                                                }
                                            />
                                        </footer>
                                    </article>
                                </div>
                            </form>
                        )}
                    </Formik>
                </section>
            </ModalWrapper>
        </>
    );
};

Form.propTypes = {
    onShowPurchase: PropTypes.func.isRequired,
    vehicle: vehicleTypes,
    removeVehicle: PropTypes.func.isRequired,
};

export default Form;
